// stores/useItemsStore.js
import { defineStore } from 'pinia';
import type { SkBillable, SkMaterial, SkEquipmentTypeConversion } from "@/assets/generated/api";
import { LineItemType, getLineItemTypeByDescription, getLineItemTypeByProductHierarchy } from "@/utils/quoteHelpers";

interface LineItemTypeMapping {
  id: null | undefined | number,
  line_item_type: LineItemType | LineItemType.UNCATEGORIZED
}

interface ApplyChangeOrderStoreState {
  billables: SkBillable[],
  billablesNotOnQuote: SkBillable[],
  lineItemTypeMapping: LineItemTypeMapping[],
  equipmentTypeConversions: SkEquipmentTypeConversion[],
  isApplyingChangeOrder: boolean,
}

/**
 * This store represents the state of billables retrieved from SK.
 *
 * @typedef {Object} SettingsState
 * @property {SkBillable[]} billables - Your array of all SkBillable objects.
 * @property {SkBillable[]} billablesNotOnQuote - same as above, just filtered.
 * @property {boolean} isApplyingChangeOrder - A conditional for triggering actions.
 * @property {SkEquipmentTypeConversion} equipmentTypeConversions - A list of
 * SK Equipment Type Conversions needed for determining line item type mappings.
 * @property {LineItemTypeMapping[]} - A mapping that calculates line item types
 * on the SGQ level. It is not a property on SkBillable, so we have to calculate
 * it in SpyGlass Quote (like we do with SkMaterials).
 */
export const useApplyChangeOrderStore = defineStore('items', {
  state: () => ({
    billables: [] as SkBillable[],
    billablesNotOnQuote: [] as SkBillable[],
    lineItemTypeMapping: [] as LineItemTypeMapping[],
    equipmentTypeConversions: [] as SkEquipmentTypeConversion[],
    isApplyingChangeOrder: false as boolean
  }),
  getters: {
    isBillablesMissingProductHierarchies: (state: ApplyChangeOrderStoreState): boolean => {
      return state.billables.some((billable: SkBillable) => {
        return !billable.prod_hierarchy;
      })
    }
  },
  actions: {
    resetStore(): void {
      this.billables = [];
      this.billablesNotOnQuote = [];
      this.lineItemTypeMapping = [];
    },
    setLineItemTypeMapping(): void {
      this.billablesNotOnQuote.forEach((billable: SkBillable) => {
        const prod_hierarchy: string | undefined | null = billable.prod_hierarchy;
        const description: string | undefined | null = billable.material_description || billable.material_long_description
        const billableExistsInMapping = this.lineItemTypeMapping.find((item: LineItemTypeMapping) => item.id === billable.id);

        const line_item_type = prod_hierarchy && this.equipmentTypeConversions.length
          ? getLineItemTypeByProductHierarchy(prod_hierarchy, this.equipmentTypeConversions) || getLineItemTypeByDescription(description) || LineItemType.UNCATEGORIZED
          : getLineItemTypeByDescription(description) || LineItemType.UNCATEGORIZED;
        
        if (billableExistsInMapping) {
          this.lineItemTypeMapping.forEach((map: LineItemTypeMapping) => {
            if (map.id === billable.id) {
              map.line_item_type = line_item_type;
            }
          })
        }
        else {
          this.lineItemTypeMapping.push({
            id: billable.id,
            line_item_type: line_item_type
          });
        }
      });
    },
    /**
     * This setter sets missing product hierarchies to this stores' {SkBillable}.
     * 
     * @param {SkMaterial[]} materials - the SK Materials containing product hierarchies
     * @returns {void} - returns nothing, just sets on stores' billables objects.
     */
    setMissingProductHierarchies(materials: SkMaterial[]): void {
      this.billablesNotOnQuote.forEach((billable: SkBillable) => {
        if (!billable.prod_hierarchy) {
          billable.prod_hierarchy = materials.find((material: SkMaterial) => {
            return material.number == billable.material_number
          })?.prod_hierarchy
        }
      })

      // If we restore missing product hierarchies, we should probably recalulate
      // the line item types and its mappings.
      this.setLineItemTypeMapping();
    },
    /**
     * This getter returns a line item type by your designated billable ID.
     * 
     * @param {number} id - the ID of the billable you want to get
     * @returns {LineItemType} - one of SGQ's line item types.
     * Note: return has fallbacks for types we are currently not supporting. We may
     * change it later (same deal with SkMaterials / MaterialSidePanel.vue).
     * 
     * // TODO: check if ID is required on the SK side, the "| null | undefined" might not be needed
     */
    getLineItemTypeByBillableID(id: number | null | undefined): LineItemType | LineItemType.UNCATEGORIZED {
      const line_item_type = this.lineItemTypeMapping.find((map: LineItemTypeMapping) => {
        return map.id === id
      })?.line_item_type || LineItemType.UNCATEGORIZED;

      switch(line_item_type) {
        case LineItemType.TRANSFORMER:
          return LineItemType.STEP_UP_TRANSFORMER;
        case LineItemType.PROTECTORS_SEALS:
          return LineItemType.SEAL;
        default:
          return line_item_type;
      }
    }
  }
});